import classNames from "classnames/bind";
import { isEqual } from "es-toolkit/compat";
import React, { useEffect, useState } from "react";
import PillToggle from "./PillToggle";
import styles from "./PillToggleGroup.module.scss";
import { PillToggleGroupProps, PillToggleProps } from "./types";

const cx = classNames.bind(styles);
type PillToggleType = Pick<PillToggleProps, "value" | "isSelected">;

/**
 * A checkbox group presented through Pill components
 */
const PillToggleGroup = ({
  name,
  pills,
  isLoading,
  onChange,
}: PillToggleGroupProps) => {
  const selectAllPillToggleIndex = pills.findIndex(
    (pill) => pill.shouldSelectAll
  );

  const [selectedPillToggles, setSelectedPillToggles] = useState<
    PillToggleType[]
  >(() =>
    pills.map(({ value, isSelected }) => {
      return { value, isSelected };
    })
  );

  const selectAllPillToggle = (pillToggles: PillToggleType[]) => {
    return pillToggles.map((pillToggle, index) => {
      return {
        ...pillToggle,
        isSelected: index === selectAllPillToggleIndex,
      };
    });
  };

  const handlePillToggleChange = (
    selectedIndex: number,
    shouldSelectAll = false
  ) => {
    let newSelectedPillToggles: PillToggleType[] = [];

    if (!shouldSelectAll) {
      newSelectedPillToggles = selectedPillToggles.map(
        (selectedPillToggle, index) => {
          return {
            ...selectedPillToggle,
            isSelected:
              index === selectedIndex
                ? !selectedPillToggle.isSelected
                : index === selectAllPillToggleIndex
                  ? false
                  : selectedPillToggle.isSelected,
          };
        }
      );
      if (
        newSelectedPillToggles.every(
          (selectedPillToggle) => !selectedPillToggle.isSelected
        )
      ) {
        newSelectedPillToggles = selectAllPillToggle(newSelectedPillToggles);
      }
    } else {
      newSelectedPillToggles = selectAllPillToggle(selectedPillToggles);
    }

    onChange?.(
      newSelectedPillToggles
        .filter((newSelectedPillToggle) => newSelectedPillToggle.isSelected)
        .map((newSelectedPillToggle) => newSelectedPillToggle.value)
    );
    setSelectedPillToggles(newSelectedPillToggles);
  };

  // we want the actual click of the user to be faster than outside state
  // changing, so we maintain internal selection state but let it reset if it
  // doesn't match up
  useEffect(() => {
    const newSelectedPillToggles = pills.map(({ value, isSelected }) => {
      return { value, isSelected };
    });

    if (isEqual(selectedPillToggles, newSelectedPillToggles)) {
      return;
    } else {
      setSelectedPillToggles(newSelectedPillToggles);
    }
  }, [pills]);

  return (
    <fieldset className={cx("container")}>
      {pills.map((pill, index) => (
        <PillToggle
          key={pill.label}
          isLoading={isLoading}
          {...pill}
          isSelected={selectedPillToggles[index]?.isSelected}
          name={name}
          onChange={() => handlePillToggleChange(index, pill.shouldSelectAll)}
        />
      ))}
    </fieldset>
  );
};

PillToggleGroup.displayName = "PillToggleGroup";

export default PillToggleGroup;
